// kernel/designer/legacy/useLegacyDesigner.ts
import { getProjectStudioViewMode } from '#lib/studio-project-view';
import { listCommandInfo } from '#lib/list/listCommandSurface';
import { listPromptUploadOwner } from '#lib/list/listPromptUploadOwner';
import { createListRetryClaim } from '#lib/list/listRetryAllowance';
import { listGenerationRecoveryStrategy } from '#lib/list/listGenerationRepair';
import { isListConfirmation, resolveListGenerationAgreement } from '#lib/list/listGenerationAgreement';
import { isListContextPreparationScript, isFactoryPersonaSetupScript, requiresListScriptPersistence } from '#lib/list/factoryReferenceLookup';
import { bindListGenerationReferences, hydrateListOutputMentions, listOutputMentionOptions, resolveListOutputMentions } from '#lib/list/listOutputMentions';
import { listContinuationBrief, waitForListMessagePersistence } from '#lib/list/listReferenceContinuation';
import { useCallback, useRef, RefObject } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { uploadFileToCloud } from '#lib/azure-client';
import { KernelHandle, AgentProjectContext, LlmTurnImage, LlmTurnVideo, Message, ScriptInstance, UiCommand, ErrorPayload, ScriptDefinition, AssistRequestBody, LockableFile, AssistantTurnDebugInfo, AgentWorkspaceContext, AgentVersionContext, WorkspaceVersion, Workspace, CollaborationMode, Layer, AgentMode, AssistantTurnTracePayload, DiffusionParams, DetachedStateClarificationPayload } from '#types/types';
import { getFileExtension } from '#lib/utils';
import { fetchApi } from '@/kernel/apiFetch';
import { DESIGNER_TURN_HEARTBEAT_S } from '#lib/designer/jnn/browserTools';
import { joinLegacyChain, leaveLegacyChain, type LegacyChain } from '#lib/designer/legacy/legacyChain';
import { sortLayersByRenderOrder } from '#lib/layer-render-order';
import { COMMAND_DEFINITIONS } from '@/components/agentCommands';
import { restrictedPlanStudioModelIds, shouldUnlockIncompatibleStudioModelPin, videoModelNeedsStartImage } from '#lib/model-registry';
import { agentCommandRestriction } from '#lib/designer/agentCommandRestriction';
import { clientAccessSuspension } from '#lib/access-suspension';
import { markGenerationTaskAccepted, markGenerationTaskAcceptedForLayer } from '#lib/generationAccept';
import { classifyResultFeedback } from '#lib/resultFeedback';
import { resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';
import { normalizeCollaborationMode } from '#lib/leasing/chat-sessions';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';
import { getProjectFocusedListOutputId, listProjectLayers } from '#lib/studio-list-focus';
import { buildListAgentContext } from '#lib/list/listAgentContext';
import {
  LEGACY_COMPAT_USER_ACTIVITIES,
  normalizeUserActivities,
  POST_SOURCING_COMMAND_ACTIONS,
  SOURCE_ONLY_COMMAND_ACTIONS,
} from '#lib/user-activities';
import { applyListAutoMode } from '@/components/list/listAutoMode';
import {
  emitAssistantMessageEventFromSession,
  evaluateDefinitionLockState,
  getCommandsForMode,
  getTruncatedChatHistory,
  markRuntimeErrorsReported,
  patchSessionRuntimeIfAbortControllerOwner,
  removeMediaGenerationCommandsForPromptRewrite,
  removePlanningCommandsForAnswerOnlyRequest,
  resolveMessageTargetFromSession,
  snapshotRuntimeErrorsForAgent,
  takeQueuedMessage,
} from '@/kernel/agentTurnShared';

const MAX_VIDEO_CONTEXT_LAYERS = 2;
const MAX_VIDEO_CONTEXT_FRAMES_PER_LAYER = 8;

const toFiniteMs = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.round(value));
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.max(0, Math.round(parsed));
  }
  return null;
};

const inferVideoMimeType = (url: string): string | undefined => {
  const normalized = url.split('?')[0]?.toLowerCase() ?? '';
  if (normalized.endsWith('.mp4') || normalized.endsWith('.m4v')) return 'video/mp4';
  if (normalized.endsWith('.webm')) return 'video/webm';
  if (normalized.endsWith('.mov') || normalized.endsWith('.qt')) return 'video/quicktime';
  return undefined;
};

const sampleVideoFrameUrls = (frameUrls: string[] | undefined, maxFrames: number): string[] => {
  const cleanFrames = (frameUrls ?? [])
    .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    .map((url) => url.trim());
  if (cleanFrames.length <= maxFrames) return cleanFrames;

  const sampled: string[] = [];
  const lastIndex = cleanFrames.length - 1;
  for (let index = 0; index < maxFrames; index += 1) {
    const sourceIndex = Math.round((index / Math.max(maxFrames - 1, 1)) * lastIndex);
    const frameUrl = cleanFrames[sourceIndex];
    if (frameUrl && !sampled.includes(frameUrl)) sampled.push(frameUrl);
  }
  return sampled;
};

const withTimeout = (
  promise: Promise<any>,
  ms: number,
  timeoutErrorMsg: string = 'Operation timed out'
): Promise<any> => {
  return Promise.race([
    promise,
    new Promise<any>((_, reject) => setTimeout(() => reject(new Error(timeoutErrorMsg)), ms)),
  ]);
};

const persistAssistantTurnTrace = async (payload: AssistantTurnTracePayload) => {
  try {
    await fetch('/api/assistant-turns', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error('[useAgents] Failed to persist assistant turn trace', error);
  }
};

/** The legacy turn's arguments as useAgents passes them: all but the turn id executeLegacyTurn gives it. */
type LegacyTurnArgs<T> = T extends (designerTurnId: string, ...args: infer Rest) => unknown ? Rest : never;

interface UseLegacyDesignerProps {
  kernel: RefObject<KernelHandle | null>;
  getProjectIdFromSessionId: (sessionId: string) => string | null;
}

export function useLegacyDesigner({
  kernel,
  getProjectIdFromSessionId,
}: UseLegacyDesignerProps) {
  const getProjectIdFromWorkspaceId = useCallback((workspaceId: string): string | null => {
    const k = kernel.current;
    if (!k) return null;
    const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
    if (!normalizedWorkspaceId) return null;
    const workspace = k.getInventory().StudioWorkspace.registry[normalizedWorkspaceId];
    const projectId = typeof workspace?.projectId === 'string' ? workspace.projectId.trim() : '';
    return projectId.length > 0 ? projectId : null;
  }, [kernel]);
  const listWorkspacesByProjectId = useCallback((projectIdInput: string): Workspace[] => {
    const k = kernel.current;
    if (!k) return [];
    const projectId = projectIdInput.trim();
    if (!projectId) return [];
    const inventory = k.getInventory();
    const project = inventory.Project.registry[projectId];
    const workspaceIds = Array.isArray(project?.workspaceIds)
      ? project.workspaceIds.filter(
          (workspaceId): workspaceId is string =>
            typeof workspaceId === 'string' && workspaceId.trim().length > 0
        )
      : [];

    return workspaceIds
      .map((workspaceId) => k.getWorkspace(workspaceId))
      .filter((workspace): workspace is Workspace => workspace !== null && workspace.projectId === projectId);
  }, [kernel]);

  const listLayersByProjectId = useCallback((projectIdInput: string): Layer[] => {
    const k = kernel.current;
    if (!k) return [];
    const projectId = projectIdInput.trim();
    if (!projectId) return [];
    const workspaces = listWorkspacesByProjectId(projectId);
    const layerRegistry = k.getLayerRegistry();
    const layerIds = new Set<string>();

    workspaces.forEach((workspace) => {
      const ids = Array.isArray(workspace.layerIds)
        ? workspace.layerIds.filter(
            (layerId): layerId is string =>
              typeof layerId === 'string' && layerId.trim().length > 0
          )
        : [];
      ids.forEach((layerId) => layerIds.add(layerId));
    });

    return Array.from(layerIds)
      .map((layerId) => layerRegistry[layerId])
      .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer));
  }, [kernel, listWorkspacesByProjectId]);

  const describeState = useCallback((workspaceId: string, expectedProjectId?: string | null): string => {
    if (!kernel.current) return '';
    const diffusionModel = kernel.current.getDiffusionModel(); // Should derive from runtime
    const params = kernel.current.getModelParams()[diffusionModel]; // Should derive from runtime
    const projectId = expectedProjectId?.trim() || getProjectIdFromWorkspaceId(workspaceId);
    const userContext = kernel.current.getUserContext?.();
    const restrictedPlanModels = restrictedPlanStudioModelIds(
      projectId ? kernel.current.getInventory().Project.registry[projectId]?.projectType : null,
      clientAccessSuspension(userContext?.accessSuspension, userContext?.organisationId, userContext?.teamId).withheldModelFamilies,
    ).join(', ');
    if (projectId && getProjectStudioViewMode(kernel.current, projectId) === 'marketing') {
      const { prompt: _previousPrompt, ...settings } = params ?? {};
      return `List surface: independent saved outputs, not a Canvas layer stack. Use the current List results inventory in project context; loaded inventory may be incomplete.
UI Selected Image/Video Model: ${JSON.stringify(diffusionModel)}
UI Parameters Snapshot: ${JSON.stringify(settings)}
These are settings, not permission to reuse output pixels.
${kernel.current.getOrganisationCredits()?.isRestricted ? `Restricted credit plan: use only ${restrictedPlanModels}.` : ''}`;
    }
    const requestedWorkspace = kernel.current.getWorkspace(workspaceId);
    const activeWorkspace = requestedWorkspace?.projectId === projectId
      ? requestedWorkspace
      : (projectId ? listWorkspacesByProjectId(projectId)[0] : undefined);
    const activeWorkspaceId = activeWorkspace?.id ?? '';
    const layers = kernel.current.getLayersInWorkspaceContext(activeWorkspaceId);
    const referenceImages = projectId ? kernel.current.getExtraFiles(projectId) : [];
    const audioReferences = projectId ? kernel.current.getAudioReferenceFiles(projectId) : [];
    const spectacleMode = kernel.current.getSpectacleMode();
    const aspectRatio = activeWorkspace?.aspectRatio ?? null;
    const organisationCredits = kernel.current.getOrganisationCredits();
    const isRestricted = organisationCredits?.isRestricted ?? false;

    if (!activeWorkspace) {
      console.warn(`[describeState] Could not find workspace with ID ${workspaceId}`);
      return '';
    }
    const generationRect = activeWorkspace.generationRect;
    const activeVersionId =
      (typeof activeWorkspace.activeVersionId === 'string' && activeWorkspace.activeVersionId.trim().length > 0)
        ? activeWorkspace.activeVersionId
        : ((typeof activeWorkspace.validatedVersionId === 'string' && activeWorkspace.validatedVersionId.trim().length > 0)
          ? activeWorkspace.validatedVersionId
          : null);
    const activeVersion = activeVersionId
      ? (activeWorkspace.versions ?? []).find((version) => version.id === activeVersionId) ?? null
      : null;

    let annotationContext = '';
    if (
      activeWorkspace.annotationsEnabled !== false &&
      activeVersion?.annotationComments &&
      activeVersion.annotationComments.length > 0
    ) {
      const commentsList = activeVersion.annotationComments
        .map((c, i) => `  [${i + 1}] At position (${Math.round(c.x)}, ${Math.round(c.y)}): "${c.text}"`)
        .join('\n');
      annotationContext = `\n      User Annotations (numbered markers on the image):\n${commentsList}`;
    }

    let restrictionContext = '';
    if (isRestricted) {
      restrictionContext = `
      *** CRITICAL CREDIT RESTRICTION ***
      The organization is on a restricted credit plan. You are STRICTLY LIMITED to using only the following models:
      ${restrictedPlanModels}
      
      Do NOT generate scripts using any other models (like nano-banana-pro, ideogram, etc.) as they will fail. Adapt your strategy to use the allowed models only.
      `;
    }

    const dynamicContext = `
      Current Editor State Summary:
      - UI Selected Image/Video Model: "${diffusionModel}"
      - UI Current Prompt: "${params && params.prompt || ''}"
      - UI Parameters Snapshot: ${JSON.stringify(params, null, 2)}
      - Project Number of Layers: ${layers.length}
      - UI Workspace ID: "${workspaceId}"
      - Workspace Generation Area : x=${generationRect.x}, y=${generationRect.y}, w=${generationRect.w}, h=${generationRect.h}
      - Workspace Generation Area Aspect Ratio Lock: ${aspectRatio ? `${aspectRatio.toFixed(2)}:1` : 'Free'}
      - Number of Model Reference Images Attached: ${referenceImages.length}
      - Number of Model Audio References Attached: ${audioReferences.length}
      - Spectacle Mode is ${spectacleMode ? 'enabled' : 'disabled'}

      ${annotationContext}
      ${restrictionContext}
    `;
    return dynamicContext.trim();
  }, [kernel]);


  const gatherProjectContext = useCallback(async (
    workspaceId: string,
    sessionId: string,
    abortSignal?: AbortSignal,
    inspectListResultsAfter?: number,
  ): Promise<{ projectContext: AgentProjectContext; systemContextImages: LlmTurnImage[]; systemContextVideos: LlmTurnVideo[] }> => {
    if (!kernel.current) throw new Error("Editor not available for gathering context.");

    const projectIdFromSession = getProjectIdFromSessionId(sessionId);
    const projectId = projectIdFromSession ?? getProjectIdFromWorkspaceId(workspaceId);
    const project = projectId
      ? kernel.current.getInventory().Project.registry[projectId]
      : null;
    const projectName = projectId
      ? kernel.current.getInventory().Project.registry[projectId]?.name
      : null;

    if (!projectId || !projectName || !project) {
      throw new Error("[gatherProjectContext] Unable to resolve project details.");
    }
    const projectOwnerId = kernel.current.getInventory().Project.registry[projectId]?.userId;

    const projectType = (project as any)?.projectType;
    const sessionCollaborationMode = normalizeCollaborationMode(
      kernel.current.getInventory().ChatSession.registry[sessionId]?.collaborationMode
    );
    const resolvedCollaborationMode: CollaborationMode =
      sessionCollaborationMode
      ?? normalizeCollaborationMode(kernel.current.getUserPreferences()?.defaultCollaborationMode)
      ?? 'ASSISTED';
    // The marketing view has no Canvas approval/version loop. Its loop is governed solely by
    // the composer's toggle; projectType remains compatibility metadata, not a destination.
    const isListView = getProjectStudioViewMode(kernel.current, projectId) === 'marketing';
    const collaborationMode: CollaborationMode =
      isListView ? 'AUTONOMOUS' : resolvedCollaborationMode;
    const allWorkspaces: Workspace[] = listWorkspacesByProjectId(projectId);
    const requestedWorkspace = kernel.current.getWorkspace(workspaceId);
    const resolvedActiveWorkspace = requestedWorkspace?.projectId === projectId
      ? requestedWorkspace
      : allWorkspaces[0] ?? null;

    if (!resolvedActiveWorkspace && !isListView) {
      throw new Error(`[gatherProjectContext] Workspace with ID ${workspaceId} not found.`);
    }
    const activeWorkspaceId = resolvedActiveWorkspace?.id ?? '';
    const activeWorkspace = resolvedActiveWorkspace ?? ({
      id: '',
      projectId,
      name: 'Output',
      layerIds: [],
      activeVersionId: null,
      generationRect: { x: 0, y: 0, w: 1024, h: 1024 },
      aspectRatio: 1,
      frameRect: { x: 0, y: 0, w: 1024, h: 1024 },
      annotationMask: null,
      annotationOpaque: false,
      expanded: false,
      locked: true,
      visible: true,
      validatedVersionId: null,
    } as Workspace);
    const generationRect = activeWorkspace.generationRect;

    const allProjectLayers: Layer[] = listLayersByProjectId(projectId);
    const layerById = new Map<string, Layer>(allProjectLayers.map(layer => [layer.id, layer]));

    const workspacesForContext: AgentWorkspaceContext[] = isListView ? [] : allWorkspaces.map(ws => {
      const validatedId = ws.validatedVersionId;
      const versionsById = new Map((ws.versions || []).map(v => [v.id, v]));
      const versionsForAI: AgentVersionContext[] = (ws.versions || [])
        .filter(v =>
          v.status === 'VALIDATED' ||
          v.status === 'CANDIDATE' ||
          (v.status === 'REJECTED' && v.parentId === validatedId)
        )
        .map(v => {
          let generativeContext: AgentVersionContext['generativeContext'] = null;

          if (v.status === 'REJECTED' && v.parentId) {
            const parentVersion = versionsById.get(v.parentId);

            if (parentVersion && parentVersion.layerVisibility) {
              const parentVisibleLayerIds = new Set(
                parentVersion.layerVisibility
                  .filter(l => l.visible)
                  .map(l => l.id)
              );

              const newVisibleLayer = v.layerVisibility?.find(layerVisibility =>
                layerVisibility.visible && !parentVisibleLayerIds.has(layerVisibility.id)
              );

              if (newVisibleLayer) {
                const sourceLayer = layerById.get(newVisibleLayer.id);

                if (sourceLayer && sourceLayer.params && 'prompt' in sourceLayer.params) {
                  const sourceParams = sourceLayer.params as DiffusionParams;
                  const promptValue = sourceParams.prompt;

                  if (typeof promptValue === 'string') {
                    generativeContext = {
                      model: sourceLayer.source,
                      prompt: promptValue,
                      params: sourceParams,
                    };
                  }
                }
              }
            }
          }

          return {
            id: v.id,
            status: v.status as 'VALIDATED' | 'CANDIDATE' | 'REJECTED',
            parentId: v.parentId,
            rejectionReason: v.rejectionReason,
            generativeContext,
          };
        });

      return {
        id: ws.id,
        name: ws.name,
        activeVersionId: ws.activeVersionId ?? null,
        validatedVersionId: ws.validatedVersionId,
        versions: versionsForAI,
      };
    });

    // How many Mask-tool regions are painted on the active workspace, drives whether the agent can
    // run masked inpainting (ideogram-inpaint) or must ask the user to paint a mask first.
    const activeWorkspaceInpaintMaskRegions =
      (kernel.current.getInpaintMaskShapes()?.[activeWorkspaceId] ?? []).length;

    const projectContext: AgentProjectContext = {
      projectId,
      projectName,
      projectType,
      studioViewMode: isListView ? 'marketing' : 'studio',
      collaborationMode,
      workspaces: workspacesForContext,
      generationRect,
      activeWorkspaceId,
      activeWorkspaceInpaintMaskRegions,
    };

    const systemContextImages: LlmTurnImage[] = [];
    const systemContextVideos: LlmTurnVideo[] = [];
    const uploadPromises: Promise<void>[] = [];
    const CANVAS_OP_TIMEOUT_MS = 60000;
    const UPLOAD_TIMEOUT_MS = 120000;

    const processVersionView = async (workspace: Workspace, version: WorkspaceVersion, label: string, resolution: number): Promise<string | null> => {
      if (!kernel.current) {
        throw new Error("[gatherProjectContext] Kernel unavailable during version processing.");
      }

      try {
        const combinedTimeout = CANVAS_OP_TIMEOUT_MS + UPLOAD_TIMEOUT_MS;
        const newImageUrl = await withTimeout(
          kernel.current.getOrRenderVersionImage(
            workspace.id,
            version.id,
            { targetPixels: resolution, assetSource: 'upload', rect: workspace.frameRect }
          ),
          combinedTimeout,
          `Timeout: Rendering for label "${label}"`
        );

        if (newImageUrl && !abortSignal?.aborted) {
          systemContextImages.push({ url: newImageUrl, label });
          return newImageUrl;
        }
      } catch (e: any) {
        console.error(`[gatherProjectContext] Error processing version view for label "${label}":`, e?.message ?? e);
      }
      return null;
    };

    const uploadAndAddToContext = (filePromise: Promise<File | null>, label: string, viewType: 'ga' | 'overlay') => {
      uploadPromises.push(
        filePromise.then(file => {
          if (file && !abortSignal?.aborted) {
            const fileUUID = uuidv4();
            const extension = getFileExtension(file.name) || 'webp';
            const persistentFileName = `assistant_${viewType}_${fileUUID}.${extension}`;
            
            return withTimeout(uploadFileToCloud(file, persistentFileName, file.type, undefined, projectOwnerId), UPLOAD_TIMEOUT_MS, `Timeout: Uploading for label "${label}"`)
              .then(url => {
                if (url) {
                  systemContextImages.push({ url, label });
                  kernel.current?.registerCloudAssets([url], 'upload');
                }
              })
              .catch(e => console.error(`[gatherProjectContext] Error uploading for label "${label}":`, e.message));
          }
          return Promise.resolve();
        })
      );
    };

    const uploadReferenceImageAndAddToContext = (refImageFile: LockableFile) => {
        uploadPromises.push((async () => {
            if (abortSignal?.aborted) return;

            const file = refImageFile.file;
            const fileUUID = uuidv4();
            const extension = getFileExtension(file.name) || 'bin';
            const persistentFileName = `ref_image_${fileUUID}.${extension}`;
            const label = `REFERENCE_IMAGE: '${file.name}'`;

            try {
                const url = await withTimeout(
                    uploadFileToCloud(file, persistentFileName, file.type, undefined, projectOwnerId),
                    UPLOAD_TIMEOUT_MS,
                    `Timeout: Uploading reference image "${file.name}"`
                );
                if (url) {
                    systemContextImages.push({ url, label });
                    kernel.current?.registerCloudAssets([url], 'upload');
                }
            } catch (e) {
                console.error(`[gatherProjectContext] Failed to upload reference image "${file.name}":`, e);
            }
        })());
    };

    if (isListView && !abortSignal?.aborted) {
      const listContext = buildListAgentContext(kernel.current, projectId, sessionId, { currentResultsAfter: inspectListResultsAfter });
      projectContext.listOutputs = listContext.listOutputs;
      systemContextImages.push(...listContext.systemContextImages);
      return { projectContext, systemContextImages, systemContextVideos };
    }

    const validatedVersion = activeWorkspace.versions?.find(v => v.id === activeWorkspace.validatedVersionId);
    if (validatedVersion) {
      const isGaAndFullViewIdentical =
        activeWorkspace.generationRect.x === activeWorkspace.frameRect.x &&
        activeWorkspace.generationRect.y === activeWorkspace.frameRect.y &&
        activeWorkspace.generationRect.w === activeWorkspace.frameRect.w &&
        activeWorkspace.generationRect.h === activeWorkspace.frameRect.h;

      const validatedLabel = isGaAndFullViewIdentical
        ? `VALIDATED_AND_ACTIVE_GA_VIEW: (ID: ${validatedVersion.id}) The current generation area matches the full validated version. Compare candidates against this single image.`
        : `VALIDATED_VERSION_VIEW: (ID: ${validatedVersion.id}) : Compare candidates images against this image`;

      const validatedViewUrlPromise = processVersionView(
        activeWorkspace,
        validatedVersion,
        validatedLabel,
        768 * 768
      );

      uploadPromises.push(validatedViewUrlPromise.then(() => {}));

      if (!isGaAndFullViewIdentical) {
        const gaViewFilePromise = withTimeout(
          kernel.current!.renderWorkspaceArea(
            workspaceId,
            undefined,
            768 * 768,
            false,
            true,
            1,
            activeWorkspace.generationRect,
            'webp'
          ),
          CANVAS_OP_TIMEOUT_MS,
          "Timeout: Rendering GA View"
        ).then(result => result?.file || null);
        uploadAndAddToContext(gaViewFilePromise, `ACTIVE_VERSION_GA_VIEW`, 'ga');
      }

      if (activeWorkspace.annotationsEnabled !== false && activeWorkspace.annotationMask) {
        const annotationFilePromise = withTimeout(
          kernel.current.renderWorkspaceArea(
            workspaceId,
            undefined,
            768 * 768,
            false,
            true,
            1,
            activeWorkspace.frameRect,
            'webp',
            undefined,
            'drawAnnotationOverlay'
          ),
          CANVAS_OP_TIMEOUT_MS,
          "Timeout: Rendering Annotation Overlay"
        ).then(result => result?.file || null);
        
        uploadAndAddToContext(
          annotationFilePromise,
          `USER_ANNOTATIONS_OVERLAY: This image contains visual markup (drawings, arrows, circles) drawn by the user. It overlays the VALIDATED_VERSION_VIEW. Use this to understand specific visual instructions.`,
          'overlay'
        );
      }

      const allCandidates = activeWorkspace.versions?.filter(v => v.status === 'CANDIDATE') || [];
      const validatedVersionId = activeWorkspace.validatedVersionId;
      let lineageCandidates = allCandidates;

      if (validatedVersionId) {
        const validAncestorIds = new Set<string>([validatedVersionId]);
        const validCandidateIds = new Set<string>();
        let foundNew = true;

        while (foundNew) {
          foundNew = false;

          for (const candidate of allCandidates) {
            if (validCandidateIds.has(candidate.id)) continue;

            const parentId = candidate.parentId;
            if (parentId && validAncestorIds.has(parentId)) {
              validCandidateIds.add(candidate.id);
              validAncestorIds.add(candidate.id);
              foundNew = true;
            }
          }
        }

        lineageCandidates = allCandidates.filter(candidate => validCandidateIds.has(candidate.id));
      }

      for (const candidate of lineageCandidates) {
        if (abortSignal?.aborted) break;
        uploadPromises.push(processVersionView(
          activeWorkspace,
          candidate,
          `CANDIDATE_VERSION_VIEW: (ID: ${candidate.id}) : Compare this image with the validated one for finding regression or progress.`,
          768 * 768
        ).then(() => {}));
      }
    }

    for (const otherWs of allWorkspaces.filter(ws => ws.id !== activeWorkspaceId)) {
      if (abortSignal?.aborted) break;
      const otherValidatedVersion = otherWs.versions?.find(v => v.id === otherWs.validatedVersionId);
      if (otherValidatedVersion) {
        uploadPromises.push(processVersionView(
          otherWs,
          otherValidatedVersion,
          `OTHER_WORKSPACE_VIEW: '${otherWs.name}' (Validated Version: '${otherValidatedVersion.name}')`,
          512 * 512
        ).then(() => {}));
      }
    }

    const referenceImages = (projectId ? kernel.current.getExtraFiles(projectId) : []) || [];
    referenceImages.forEach(refImageFile => {
      if (abortSignal?.aborted) return;
      uploadReferenceImageAndAddToContext(refImageFile);
    });

    const visibleActiveVideoLayers = sortLayersByRenderOrder(
      kernel.current.getLayersInWorkspaceContext(activeWorkspaceId)
        .filter((layer): layer is Layer => Boolean(layer))
        .filter((layer) => layer.type === 'video' && layer.visible !== false && typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0)
    ).slice(-MAX_VIDEO_CONTEXT_LAYERS);

    visibleActiveVideoLayers.forEach((layer, layerIndex) => {
      if (abortSignal?.aborted) return;

      const videoUrl = layer.videoSrc?.trim();
      if (!videoUrl) return;

      const timelineStartMs = toFiniteMs(layer.timelineStartMs);
      const timelineEndMs = toFiniteMs(layer.timelineEndMs);
      const sourceInMs = toFiniteMs(layer.timelineSourceInMs);
      const trackDurationMs =
        timelineStartMs !== null && timelineEndMs !== null && timelineEndMs > timelineStartMs
          ? timelineEndMs - timelineStartMs
          : null;
      const cursorMs = toFiniteMs((activeWorkspace as { timelineCursorMs?: unknown }).timelineCursorMs);
      const frameUrls = Array.isArray(layer.videoFramesUrls) ? layer.videoFramesUrls : [];
      const sampledFrameUrls = sampleVideoFrameUrls(frameUrls, MAX_VIDEO_CONTEXT_FRAMES_PER_LAYER);
      const layerName = layer.name || layer.id;
      const baseLabelParts = [
        `ACTIVE_WORKSPACE_VIDEO: Layer "${layerName}" (ID: ${layer.id}) in workspace "${activeWorkspace.name || activeWorkspaceId}"`,
        `order=${layerIndex + 1}/${visibleActiveVideoLayers.length}`,
        trackDurationMs !== null ? `timelineDurationMs=${trackDurationMs}` : null,
        timelineStartMs !== null ? `timelineStartMs=${timelineStartMs}` : null,
        timelineEndMs !== null ? `timelineEndMs=${timelineEndMs}` : null,
        sourceInMs !== null ? `timelineSourceInMs=${sourceInMs}` : null,
        cursorMs !== null ? `workspaceCursorMs=${cursorMs}` : null,
        frameUrls.length > 0 ? `cachedFrameCount=${frameUrls.length}` : null,
      ].filter(Boolean);
      const label = `${baseLabelParts.join('; ')}. Use this raw video for temporal understanding when the model supports video. Sampled still frames are also attached as fallback context.`;

      systemContextVideos.push({
        url: videoUrl,
        label,
        mimeType: inferVideoMimeType(videoUrl),
        layerId: layer.id,
        durationMs: trackDurationMs,
      });

      sampledFrameUrls.forEach((frameUrl, frameIndex) => {
        systemContextImages.push({
          url: frameUrl,
          label: `ACTIVE_WORKSPACE_VIDEO_FRAME: Layer "${layerName}" (ID: ${layer.id}) sample ${frameIndex + 1}/${sampledFrameUrls.length}. This is a sampled still from the active workspace video; use it as provider-neutral fallback if raw video is unavailable.`,
        });
      });
    });

    await Promise.all(uploadPromises);

    if (abortSignal?.aborted) {
      throw new Error("Visual context gathering aborted during uploads.");
    }

    return { projectContext, systemContextImages, systemContextVideos };
  }, [kernel, listLayersByProjectId, listWorkspacesByProjectId, getProjectIdFromWorkspaceId, getProjectIdFromSessionId]);

  const _executeAssistantTurn = useCallback(async (
    designerTurnId: string,
    sessionId: string,
    workspaceId: string,
    promptOrGoalOrInstruction: string,
    images: LockableFile[],
    isUserTurn: boolean,
    imageUrls: string[] = [],
    agentMode: AgentMode,
    onScriptComplete?: (finalWorkspaceId: string) => Promise<void>,
    lastScriptError?: ErrorPayload,
    progressiveStepRetryCount?: number,
    skipUserMessage: boolean = false,
    ignoreScripts: boolean = false,
    customChatHistory?: Message[],
    targetChatSessionId?: string | null
  ): Promise<{ script: Promise<void> } | void> => {
    // Set when the turn is refused before reaching the model: the user's message goes back to the composer.
    let restoreComposerAfterRefusal = false;
    if (!kernel.current) {
        console.error("[useAgents] EditorRef is not available.");
        return;
    }

    const session = kernel.current.getInventory().ChatSession.registry[sessionId];
    if (!session) return;
    takeQueuedMessage(kernel.current, sessionId);
    const activeAnchors = session?.workingMemory?.anchors || [];
    const activeSubjectCandidate = session?.activeSubject ?? null;
    const activeSubject = isEntityFocusSubject(activeSubjectCandidate)
      ? activeSubjectCandidate
      : null;

    const messages = customChatHistory || kernel.current.getChatSessionMessages(session.id);
    const userPreferences = kernel.current.getUserPreferences();
    const actionHistory = kernel.current.getActionHistory();
    const strategicPlan = session.strategicPlan;
    const finalTargetSessionId = targetChatSessionId || sessionId;
    const isFactoryCommandTurn = /^\s*\/factory\b/i.test(promptOrGoalOrInstruction ?? '');
    // List Auto mode starts with the turn, in parallel with its preparation: the typed message is
    // shown at once and the version with mentions is awaited only where the turn reads it.
    const autoProjectId = isUserTurn && !skipUserMessage && !isFactoryCommandTurn ? kernel.current.getLastProjectId(sessionId) : null;
    const technicalPromptPromise = autoProjectId && getProjectStudioViewMode(kernel.current, autoProjectId) === 'marketing'
      ? applyListAutoMode(kernel.current, autoProjectId, promptOrGoalOrInstruction, messages) : null;
    let turnPrompt = promptOrGoalOrInstruction;

    if (kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested) {
      return;
    }

    const agreementProjectId = kernel.current.getLastProjectId(sessionId);
    let agreedFailedRequest = false;
    const eligibleAgreementId = kernel.current.readEntityRuntimeState('ChatSession', sessionId).listGenerationAgreementScriptId;
    if (isUserTurn) kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { listGenerationAgreementScriptId: null });
    if (isUserTurn && agreementProjectId && getProjectStudioViewMode(kernel.current, agreementProjectId) === 'marketing'
      && isListConfirmation(promptOrGoalOrInstruction)) {
      const agreement = resolveListGenerationAgreement(promptOrGoalOrInstruction, messages, {
        projectId: agreementProjectId, sessionId, userId: kernel.current.getUserContext().dbUserId ?? '',
      });
      const latestTaskIds = kernel.current.readEntityRuntimeState('ChatSession', sessionId).latestListGenerationTaskIds;
      const failedTask = latestTaskIds?.length === 1 ? kernel.current.getGenerationTasks(agreementProjectId).find(task => task.id === latestTaskIds[0]) : undefined;
      const mayResume = agreement?.script?.listGenerationAgreement?.proposal || (failedTask && listGenerationRecoveryStrategy(failedTask)
        && !failedTask.layer && !failedTask.recoveryPending && failedTask.chatSessionId === sessionId);
      if (agreement?.script && agreement.script.id === eligibleAgreementId && mayResume) {
      agreedFailedRequest = !agreement.script.listGenerationAgreement!.proposal;
      kernel.current.updateActiveSessionMessages(previous => previous.map(message => ({ ...message,
        scriptsInstances: message.scriptsInstances?.map(script => script.id === agreement.script!.id
          ? { ...script, listGenerationAgreement: { ...script.listGenerationAgreement!, consumed: true } } : script),
      })), sessionId);
      }
    }

    if (isFactoryCommandTurn) {
      kernel.current.setSessionStatus(sessionId, '');
    } else {
      kernel.current.setSessionStatus(sessionId, 'Uploading...');
    }
    let errorOccurred = false;
    const currentAssistantAbortController = new AbortController();
    kernel.current.patchEntityRuntimeState('ChatSession', sessionId, {
        abortController: currentAssistantAbortController,
        isStopRequested: false
    });
    
    let allUploadsStartTime: number;
    let allUploadsEndTime: number;
    let apiCallStartTime: number | null = null;
    let apiCallEndTime: number | null = null;

    let shouldProcessAndRunScripts = true;
    let projectContextForThisTurn: AgentProjectContext | null = null;
    let listRetryAllowanceForTurn = kernel.current.readEntityRuntimeState('ChatSession', sessionId).listRetryAllowance;
    if (isUserTurn) {
      const retryProjectId = kernel.current.getLastProjectId(sessionId);
      const claim = retryProjectId && getProjectStudioViewMode(kernel.current, retryProjectId) === 'marketing'
        ? createListRetryClaim(agreedFailedRequest ? 'try again' : promptOrGoalOrInstruction, true) : undefined;
      const retryUserId = kernel.current.getUserContext().dbUserId;
      const latestIds = kernel.current.readEntityRuntimeState('ChatSession', sessionId).latestListGenerationTaskIds;
      const sourceTaskId = latestIds?.length === 1 ? latestIds[0]! : null;
      const sourceTask = sourceTaskId && retryProjectId ? kernel.current.getGenerationTasks(retryProjectId).find(task => task.id === sourceTaskId) : undefined;
      // The saved-request replay only covers one transient failure. Any other "try again" (several
      // failed images, a refused input) goes to the agent like any message, instead of stopping on a toast.
      listRetryAllowanceForTurn = claim && retryProjectId && retryUserId && (agreedFailedRequest || (sourceTask?.error && listGenerationRecoveryStrategy(sourceTask)))
        ? { claim, projectId: retryProjectId, userId: retryUserId, sourceTaskId }
        : undefined;
      kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { listRetryAllowance: listRetryAllowanceForTurn });
    }
    let systemContextImagesForThisTurn: LlmTurnImage[] = [];
    let messageTimestampForStream: string | null = null;
    let turnIdForStream: string | null = null;
    let processedScriptForStream: ScriptInstance | null = null;
    let thoughtsForStream: string = '';
    let lastActivityHeading = '';
    let assistantResponseContent = '';

    let assistBody: AssistRequestBody | null = null;

    if (isUserTurn && listRetryAllowanceForTurn && (images.length > 0 || imageUrls.length > 0)) {
      kernel.current.handleError('Retry keeps the failed image’s saved inputs. Describe a new edit to use new attachments, or remove them to retry unchanged.');
      kernel.current.setSessionStatus(sessionId, '');
      patchSessionRuntimeIfAbortControllerOwner(kernel.current, sessionId, currentAssistantAbortController, { abortController: null });
      return;
    }

    try {
      if (isUserTurn){
        // THINKING STARTS ON SEND, NOT ON FETCH. This used to fire just before the network call,
        // which is AFTER the whole turn is assembled: the relational context, the doctrine, the
        // session history, the images. On a Source conversation that is seconds of silence with
        // the message already posted, so the agent looked like it said nothing and then abruptly
        // answered. Reported on 2026-08-16. The later call is now a no-op for typed turns.
        if (!skipUserMessage && !isFactoryCommandTurn) kernel.current.startThinkingSequence?.(sessionId);
        kernel.current.setChatPrompt('');
        kernel.current.setImagesInChatInput([]);
        if (!skipUserMessage && (images.length + imageUrls.length) === 0) {
          kernel.current.addUserMessage(promptOrGoalOrInstruction, undefined, technicalPromptPromise ? { technicalPrompt: technicalPromptPromise } : undefined);
        }

        if (listRetryAllowanceForTurn) {
          const allowance = listRetryAllowanceForTurn;
          const runtime = kernel.current.readEntityRuntimeState('ChatSession', sessionId);
          if (runtime.listRetryAllowance?.claim !== allowance.claim || runtime.isStopRequested
            || allowance.userId !== kernel.current.getUserContext().dbUserId
            || allowance.projectId !== kernel.current.getLastProjectId(sessionId)
            || kernel.current.getActiveChatSessionId() !== sessionId) return;
          if (!allowance.sourceTaskId) throw new Error('Choose the failed image’s Retry button so I can recover the exact request.');
          // Retry authority is the saved task, not an LLM's reconstruction of old @references.
          const retryScript: ScriptInstance = {
            id: uuidv4(), label: 'Retry failed image', autorun: true, autoRecall: false,
            studioViewMode: 'marketing', isAgentGenerated: true,
            commands: [{ id: uuidv4(), action_type: 'GENERATE_MEDIA', parameters: {}, status: 'pending' }],
          };
          void kernel.current.runGeneratedScriptLogic(workspaceId, retryScript, onScriptComplete, {
            sessionId, claimListRetryAllowance: allowance.claim, listRetrySourceTaskId: allowance.sourceTaskId,
          }).catch(error => kernel.current?.handleError(error instanceof Error ? error.message : 'The saved image retry could not start.'));
          return;
        }

        // Studio Cortex T3: learn from what the user SAYS about the last result, not
        // only from an explicit favourite. In a LIST project, if this message clearly
        // approves or rejects the result they were just shown, record it as the user's
        // keep/reject on that output, the same signal favouriting produces. Ambiguous
        // messages and plain refinement requests ("make it bigger") record nothing.
        try {
          const feedbackProjectId = getProjectIdFromSessionId(sessionId) ?? getProjectIdFromWorkspaceId(workspaceId);
          const isListProject = !!feedbackProjectId && getProjectStudioViewMode(kernel.current, feedbackProjectId) === 'marketing';
          if (isListProject) {
            const feedback = classifyResultFeedback(promptOrGoalOrInstruction);
            if (feedback) {
              const latestProjectLayer = listProjectLayers(kernel.current, feedbackProjectId)
                .filter((layer) => Boolean(layer.imageSrc || layer.videoFramesUrls?.length))
                .sort((left, right) => (
                  (right.timestamp ?? 0) - (left.timestamp ?? 0)
                  || right.id.localeCompare(left.id)
                ))[0];
              if (latestProjectLayer) {
                void markGenerationTaskAcceptedForLayer(
                  latestProjectLayer.id,
                  feedbackProjectId,
                  feedback === 'approve',
                );
              } else {
                const feedbackWorkspace = listWorkspacesByProjectId(feedbackProjectId).find((candidate) => candidate.id === workspaceId)
                  ?? listWorkspacesByProjectId(feedbackProjectId)[0];
                const versions = feedbackWorkspace?.versions ?? [];
                let lastOutput: { id: string; timestamp: number } | null = null;
                for (const v of versions) {
                  if (!lastOutput || v.timestamp > lastOutput.timestamp) lastOutput = v;
                }
                if (lastOutput) void markGenerationTaskAccepted(lastOutput.id, feedback === 'approve');
              }
            }
          }
        } catch (feedbackError) {
          console.warn('[Cortex] chat-feedback capture failed', feedbackError);
        }
      }

      const situationPromise = Promise.resolve(describeState(
        workspaceId,
        getProjectIdFromSessionId(sessionId) ?? getProjectIdFromWorkspaceId(workspaceId),
      ));
      
      const normalizedActivities = normalizeUserActivities(userPreferences.activities, {
        fallback: LEGACY_COMPAT_USER_ACTIVITIES,
      });
      const canAccessSource = normalizedActivities.includes('SOURCE');

      // Web search is always available, see the note at the other call site.
      const internalDbEnabled = kernel.current?.getInternalDbEnabled() ?? true;
      const isListCommandSurface = agentMode === 'director' && kernel.current
        && getProjectStudioViewMode(kernel.current,
          getProjectIdFromSessionId(sessionId) ?? getProjectIdFromWorkspaceId(workspaceId)) === 'marketing';
      const allAvailableCommands = getCommandsForMode(agentMode)
        .flatMap(command => isListCommandSurface ? listCommandInfo(command) ?? [] : [command])
        .filter((cmd) => canAccessSource || !SOURCE_ONLY_COMMAND_ACTIONS.has(cmd.action_type))
          .filter((cmd) => !POST_SOURCING_COMMAND_ACTIONS.has(cmd.action_type))
        .filter((cmd) => internalDbEnabled || cmd.action_type !== 'QUERY')
        .map(cmd => {
          const def = COMMAND_DEFINITIONS[cmd.action_type];
          const lockState = evaluateDefinitionLockState(def, {
            kernel,
            activeSubject,
            anchors: activeAnchors,
            activeProjectId: getProjectIdFromWorkspaceId(workspaceId) ?? getProjectIdFromSessionId(sessionId),
          });
          if (lockState?.isLocked) {
              return {
                  ...cmd,
                  description: `[LOCKED] ${lockState.unlockHint}`,
                  parameters: [],
                  isLocked: true,
                  unlockHint: lockState.unlockHint
              };
          }
          return cmd;
      });
      const activeStudioProjectId = getProjectIdFromWorkspaceId(workspaceId) ?? getProjectIdFromSessionId(sessionId);
      const lockedModelIncompatible = shouldUnlockIncompatibleStudioModelPin(
        userPreferences.lockSelectedModelForAgent === true,
        kernel.current?.getDiffusionModel() ?? '',
        activeStudioProjectId
          ? kernel.current?.getInventory().Project.registry[activeStudioProjectId]?.projectType
          : null,
      );
      const commandsWithStatus = allAvailableCommands.map(cmd => {
        const restriction = agentCommandRestriction(cmd.action_type, userPreferences, lockedModelIncompatible);
        if (restriction === 'disabled') {
          return {
            ...cmd,
            description: `[DISABLED BY USER] This command is currently disabled in user preferences and will fail if executed. Original description: ${cmd.description}`
          };
        }
        if (restriction === 'model-locked') {
          return {
            ...cmd,
            description: `[LOCKED BY USER] This command cannot run: the agent can't change the selected model. If you need it, tell the user to pick another model with the model button beside the message box (clicking the active one returns to Auto), or to turn off Settings > Models > Lock selected Studio model. Original description: ${cmd.description}`,
          };
        }
        return cmd;
      });
      const availableCommandsPromise = Promise.resolve(commandsWithStatus);

      allUploadsStartTime = performance.now();
      const inspectListResultsAfter = isUserTurn ? undefined : Date.parse(messages.findLast(message => message.role === 'user')?.timestamp ?? '');
      const projectContextPromise = gatherProjectContext(
        workspaceId,
        sessionId,
        currentAssistantAbortController.signal,
        inspectListResultsAfter,
      );
      const projectIdForPromptUploads = getProjectIdFromSessionId(sessionId) ?? getProjectIdFromWorkspaceId(workspaceId);
      const projectOwnerIdForPromptUploads = listPromptUploadOwner(
        projectIdForPromptUploads ? kernel.current.getInventory().Project.registry[projectIdForPromptUploads] : undefined,
        kernel.current.getUserContext().dbUserId,
      );

      const promptImageUploadPromises = images.map(lockableFile => {
        const filename = `${Date.now()}${Math.random().toString(36).substring(2, 4)}.${getFileExtension(lockableFile.file.name) || 'bin'}`;
        return uploadFileToCloud(lockableFile.file, filename, lockableFile.file.type, undefined, projectOwnerIdForPromptUploads)
          .catch(e => {
            console.error(`Error uploading prompt-specific file ${lockableFile.file.name} to user storage:`, e);
            return null;
          });
      });

      if (kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested || currentAssistantAbortController.signal.aborted) {
        currentAssistantAbortController.abort();
        throw new Error("Assistant turn aborted during context/file preparation.");
      }

      const [
        situation,
        availableCommandsForLlm,
        gatheredContext,
        uploadedUserAttachedImageUrlsForThisTurn,
      ] = await Promise.all([
        situationPromise,
        availableCommandsPromise,
        projectContextPromise,
        Promise.all(promptImageUploadPromises),
      ]);

      allUploadsEndTime = performance.now();
      console.log(`[useAgents._executeAssistantTurn] Total time for all image uploads (context & user-attached): ${(allUploadsEndTime - allUploadsStartTime).toFixed(2)}ms`);

      const uploadedPromptImageUrls = uploadedUserAttachedImageUrlsForThisTurn.filter((url): url is string => !!url);
      // A turn about an image must not run without it: the model would invent one instead.
      if (uploadedPromptImageUrls.length < images.length) {
        restoreComposerAfterRefusal = true;
        throw new Error('Your attached image could not be uploaded, so nothing was sent. Your message and image are back in the box: send them again.');
      }
      if (uploadedPromptImageUrls.length > 0) {
        kernel.current.registerCloudAssets(uploadedPromptImageUrls, 'upload');
      }

      const {
        projectContext: resolvedProjectContextForThisTurn,
        systemContextImages: resolvedSystemContextImages,
        systemContextVideos: resolvedSystemContextVideos,
      } = gatheredContext;
      projectContextForThisTurn = resolvedProjectContextForThisTurn;
      systemContextImagesForThisTurn = resolvedSystemContextImages;
      const systemContextVideosForThisTurn = resolvedSystemContextVideos;
      const listReferenceProjectId = projectContextForThisTurn?.studioViewMode === 'marketing'
        ? projectContextForThisTurn.projectId : null;
      const originatingUserMessage = !isUserTurn && listReferenceProjectId
        ? messages.findLast(message => message.role === 'user') : undefined;
      if (technicalPromptPromise) turnPrompt = await technicalPromptPromise;
      const listReferenceInstruction = listReferenceProjectId ? (isUserTurn ? turnPrompt : originatingUserMessage?.technicalPrompt ?? originatingUserMessage?.content ?? '') : '';
      if (listReferenceProjectId) await hydrateListOutputMentions(kernel.current, listReferenceProjectId, listReferenceInstruction, currentAssistantAbortController.signal);
      const listMentionResolution = resolveListOutputMentions(
        listReferenceInstruction,
        listReferenceProjectId ? listOutputMentionOptions(kernel.current, listReferenceProjectId) : [],
      );
      if (listReferenceProjectId && listMentionResolution.outputs.length) {
        projectContextForThisTurn.listOutputs = buildListAgentContext(kernel.current, listReferenceProjectId, sessionId,
          { outputIds: listMentionResolution.outputs.map(output => output.id), currentResultsAfter: inspectListResultsAfter }).listOutputs;
      }
      for (const output of listMentionResolution.outputs) {
        systemContextImagesForThisTurn.push({ url: output.url,
          label: `REFERENCE_IMAGE: @${output.handle}, ${output.name}. Explicitly mentioned this turn. Keep @${output.handle} in generation prompts; the runner assigns its final image position. ${output.url === listMentionResolution.editSourceUrl ? 'Explicit edit target, selected by the user. Use this image as sourceImageUrl and preserve unchanged features.' : 'Use as a reference; only use as base pixels if the user requests an edit.'}`,
        });
      }

      // Expose chat-input attachments as explicit REFERENCE_IMAGE entries too,
      // so model commands that support references can consume them directly.
      const seenReferenceUrls = new Set(
        resolvedSystemContextImages
          .filter((img) => typeof img.label === 'string' && img.label.startsWith('REFERENCE_IMAGE'))
          .map((img) => img.url)
      );
      images.forEach((lockableFile, index) => {
        const url = uploadedUserAttachedImageUrlsForThisTurn[index];
        if (!url || seenReferenceUrls.has(url)) return;
        const fileName = lockableFile.file?.name ?? `chat_reference_${index}`;
        systemContextImagesForThisTurn.push({
          url,
          label: `REFERENCE_IMAGE: '${fileName}'`,
        });
        seenReferenceUrls.add(url);
      });

      // LIST projects take their references SOLELY from the chat (images the
      // user attaches this turn, handled above as REFERENCE_IMAGE entries).
      // There is no canvas/focused-output reference: an output focused in the
      // batch view must never ride the turn as a reference.

      const userAttachedLlmImages: LlmTurnImage[] = [];
      const seenUrls = new Set<string>();

      images.forEach((file, index) => {
        const url = uploadedUserAttachedImageUrlsForThisTurn[index];
        if (url && !seenUrls.has(url)) {
          userAttachedLlmImages.push({
            url,
            label: `User-attached image (current turn)`,
          });
          seenUrls.add(url);
        }
      });

      imageUrls.forEach(url => {
        if (url && !seenUrls.has(url)) {
            userAttachedLlmImages.push({
                url,
                label: 'User-attached image (current turn)',
            });
            seenUrls.add(url);
        }
      });

      const MAX_USER_IMAGES = 20;
      if (userAttachedLlmImages.length < MAX_USER_IMAGES) {
          let userTurnsAgo = 0;
          for (const msg of [...messages].reverse()) {
              if (userAttachedLlmImages.length >= MAX_USER_IMAGES) break;
              if (msg.role === 'user') {
                  userTurnsAgo++;
                  if (msg.imageUrls) {
                      for (const url of msg.imageUrls) {
                          if (userAttachedLlmImages.length >= MAX_USER_IMAGES) break;
                          if (url && !seenUrls.has(url)) {
                              const promptSnippet = (msg.content || '').substring(0, 75);
                              userAttachedLlmImages.push({
                                  url,
                                  label: `User-attached image (from ${userTurnsAgo} user turn(s) ago, with prompt: "${promptSnippet}...")`,
                              });
                              seenUrls.add(url);
                          }
                      }
                  }
              }
          }
      }

      // Guarded by seenUrls like every other push above. Without it this re-added every image
      // already inserted for this turn, so one pasted logo reached the model three times: once
      // as a REFERENCE_IMAGE, once as "current turn", then again here.
      imageUrls.forEach(url => {
        if (!url || seenUrls.has(url)) return;
        userAttachedLlmImages.push({
          url,
          label: 'User-attached image',
        });
        seenUrls.add(url);
      });

      if (isUserTurn && !skipUserMessage && (images.length > 0 || imageUrls.length > 0)) {
        const allImageUrlsForMessage = [
          ...uploadedUserAttachedImageUrlsForThisTurn.filter(u => u),
          ...imageUrls
        ];
        kernel.current.addUserMessage(promptOrGoalOrInstruction, allImageUrlsForMessage as string[], technicalPromptPromise ? { technicalPrompt: technicalPromptPromise } : undefined);
      }

      if (listReferenceProjectId) {
        await waitForListMessagePersistence(kernel.current.readEntityRuntimeState('ChatSession', sessionId).messagePersistence, currentAssistantAbortController.signal);
        if (kernel.current.getActiveChatSessionId() !== sessionId || kernel.current.getLastProjectId(sessionId) !== listReferenceProjectId) {
          throw new Error('The conversation changed while saving the request.');
        }
      }

      if (kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested || currentAssistantAbortController.signal.aborted) {
        currentAssistantAbortController.abort();
        throw new Error("Assistant turn aborted before API call.");
      }

      const truncatedMessages = getTruncatedChatHistory(messages);
      const chatHistoryForApi = truncatedMessages.map((msg: Message) => {
        const apiMsg: Message = {
          ...msg,
          content: msg.role === 'user' && msg.technicalPrompt ? msg.technicalPrompt : typeof msg.content === 'string' ? msg.content : '',
          // A jnn turn's provider items: this brain reads its prose, never them.
          designerItems: undefined,
        };

        if (msg.role === 'user' && msg.imageUrls && msg.imageUrls.length > 0) {
          apiMsg.imageUrls = msg.imageUrls;
        }
        if (msg.scriptsInstances && msg.scriptsInstances.length > 0) {
          apiMsg.scriptsInstances = msg.scriptsInstances;
        }
        return apiMsg;
      });

      const systemContextLlmImages: LlmTurnImage[] = [];

      systemContextLlmImages.push(...resolvedSystemContextImages);

      assistBody = {
        prompt: isUserTurn ? turnPrompt : '',
        isUserTurn,
        situation,
        lastScriptError: lastScriptError,
        progressiveStepRetryCount,
        systemContextImages: systemContextLlmImages,
        systemContextVideos: systemContextVideosForThisTurn,
        userAttachedImages: userAttachedLlmImages,
        available_commands: availableCommandsForLlm,
        chatHistory: chatHistoryForApi,
        projectContext: projectContextForThisTurn,
        chatSessionContext: { id: sessionId, name: session.name, messageCount: messages.length },
        designerTurnId,
        userPreferences,
        actionHistory,
        strategicPlan,
        mode: agentMode,
        runtimeErrors: snapshotRuntimeErrorsForAgent(kernel),
      };
      markRuntimeErrorsReported(kernel);

      const requestBody = assistBody;

      turnIdForStream = uuidv4();
      const debugInfo: AssistantTurnDebugInfo = {
        turnId: turnIdForStream,
        requestBody,
      };
      kernel.current.setAssistantTurnDebugLog(prev => [...prev, debugInfo]);

      // A REACTION DOES NOT THINK. The user did not ask anything - they clicked something, and
      // they are watching the screen react. Announcing "Thinking" at them makes an instant reply
      // look like deliberation, which is the exact impression this was meant to remove. Real
      // telemetry from the server (searching, reading, scoring) still streams in through
      // setSessionStatus; what is suppressed is only the placeholder we invent before the first
      // token. A turn the user typed still says it is thinking, because there they ARE waiting.
      if (!skipUserMessage && !isUserTurn && !isFactoryCommandTurn) kernel.current.startThinkingSequence?.(sessionId);

      apiCallStartTime = performance.now();
      const response = await fetchApi('/api/ai/assist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: currentAssistantAbortController.signal,
      });
      apiCallEndTime = performance.now();


      if (kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested || currentAssistantAbortController.signal.aborted) {
        console.log(`[useAgents._executeAssistantTurn] /api/ai/assist call duration (aborted post-call): ${(apiCallEndTime - apiCallStartTime).toFixed(2)}ms`);
        throw new Error("Assistant turn aborted after API call, before processing response.");
      }

      if (!response.ok) {
        if (response.status === 403) {
          kernel.current?.handleNotEnoughCreditsError();
          errorOccurred = true;
        } else {
          const errorData = await response.json().catch(() => ({ message: `Request failed with status ${response.status}` }));
          console.error(`[useAgents._executeAssistantTurn] Assist API error: ${response.status} (took ${(apiCallEndTime - apiCallStartTime).toFixed(2)}ms)`, errorData);
          // A refused or failed turn says so: a silent one looked like the send did nothing (2026-10-27).
          kernel.current?.handleError(typeof errorData?.message === 'string' ? errorData.message : 'The assistant could not complete this request. Please try again.');
          errorOccurred = true;
        }
      } else if (!response.body) {
        console.error("[useAgents] Response body is null, cannot process stream.");
        errorOccurred = true;
      } else {
        console.log(`[useAgents._executeAssistantTurn] /api/ai/assist call successful in ${(apiCallEndTime - apiCallStartTime).toFixed(2)}ms, starting stream processing.`);
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        const streamStartTime = performance.now();

        while(true) {
            const { done, value } = await reader.read();
            if (currentAssistantAbortController.signal.aborted
              || kernel.current.readEntityRuntimeState('ChatSession', sessionId).abortController !== currentAssistantAbortController) {
              errorOccurred = true;
              break;
            }
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const json = line.substring(6);
                        const event = JSON.parse(json);
                        
                        if (event.type === 'error') {
                            console.error("[useAgents] Received error event from backend stream:", event.payload);
                            errorOccurred = true;
                            break;
                        }
                        switch (event.type) {
                            case 'assistantMessageAppend': {
                                const textToAppend = event.payload as string;
                                if (messageTimestampForStream && textToAppend) {
                                    kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
                                        msg.timestamp === messageTimestampForStream
                                            ? {
                                                ...msg,
                                                content: (userPreferences.actSilently !== false && processedScriptForStream)
                                                  ? ''
                                                  : (msg.content || '') + textToAppend,
                                              }
                                            : msg
                                    ), finalTargetSessionId);
                                    if (!(userPreferences.actSilently !== false && processedScriptForStream)) {
                                      assistantResponseContent += textToAppend;
                                    }
                                }
                                break;
                            }
                            case 'assistantMessage': {
                                const assistantPayload = typeof event.payload === 'string' ? event.payload : '';
                                if (!messageTimestampForStream) {
                                    messageTimestampForStream = new Date().toISOString();
                                    const imageUrlsForAssistantMessage: string[] = [];
                                    systemContextImagesForThisTurn.forEach(img => {
                                        if (img.url) imageUrlsForAssistantMessage.push(img.url);
                                    });
                                    const sessionSnapshot = finalTargetSessionId
                                      ? (kernel.current?.getInventory().ChatSession.registry[finalTargetSessionId] ?? null)
                                      : null;
                                    const messageTarget = resolveMessageTargetFromSession(sessionSnapshot);

                                    const newMessage: Message = {
                                        content: (userPreferences.actSilently !== false && processedScriptForStream) ? '' : event.payload,
                                        role: 'assistant',
                                        timestamp: messageTimestampForStream,
                                        assistantTurnId: turnIdForStream || undefined,
                                        agentName: 'Fater',
                                        thoughts: thoughtsForStream,
                                        imageUrls: [...new Set(imageUrlsForAssistantMessage)],
                                        ...messageTarget,
                                    };
                                    kernel.current?.updateActiveSessionMessages(prev => [...prev, newMessage], finalTargetSessionId);
                                } else {
                                    kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
                                        msg.timestamp === messageTimestampForStream
                                            ? {
                                                ...msg,
                                                content: (userPreferences.actSilently !== false && processedScriptForStream) ? '' : event.payload,
                                              }
                                            : msg
                                    ), finalTargetSessionId);
                                }
                                assistantResponseContent = (userPreferences.actSilently !== false && processedScriptForStream)
                                  ? ''
                                  : assistantPayload;
                                break;
                            }
                            case 'script': {
                                if (ignoreScripts) {
                                    processedScriptForStream = null;
                                    break;
                                }
                                const scriptPayload = event.payload as ScriptDefinition;
                                if (scriptPayload?.commands?.length > 0 || scriptPayload?.listGenerationAgreement?.proposal) {
                                    const autoRecallProvided =
                                      typeof scriptPayload === 'object' &&
                                      scriptPayload !== null &&
                                      Object.prototype.hasOwnProperty.call(scriptPayload, 'autoRecall');
                                    const rawResolvedAutoRecall = scriptPayload.autoRecall ?? true;
                                    const turnCollaborationMode =
                                      projectContextForThisTurn?.collaborationMode ?? 'ASSISTED';
                                    // Opening a table no longer suppresses auto-recall (the agent keeps working
                                    // after it); runaway identical re-opens are stopped in useCommands.
                                    //
                                    // Generations stay single-pass. A read-only reference lookup may
                                    // continue to complete the requested rename.
                                    const isListTurn = projectContextForThisTurn?.studioViewMode === 'marketing';
                                    const resolvedAutoRecall = isListTurn
                                      ? (isListContextPreparationScript(scriptPayload.commands) || isFactoryPersonaSetupScript(scriptPayload.commands)) && rawResolvedAutoRecall
                                      : turnCollaborationMode === 'ASSISTED'
                                        ? false
                                        : rawResolvedAutoRecall;
                                    const currentTurnSourceUrls = [
                                      ...uploadedPromptImageUrls,
                                      ...imageUrls,
                                      ...(originatingUserMessage?.imageUrls ?? []),
                                      ...listMentionResolution.outputs.map(output => output.url),
                                    ];
                                    let generationMentions = listMentionResolution;
                                    const handoff = scriptPayload.listReferenceContinuation;
                                    if (isListTurn && handoff && listReferenceProjectId) {
                                      if (currentTurnSourceUrls.length) throw new Error('The current references replace the previous edit. Send this request with its current references only.');
                                      const currentHuman = kernel.current.getChatSessionMessages(sessionId).findLast(message => message.role === 'user');
                                      if (currentHuman?.timelineEventId && currentHuman.timelineEventId !== ('rootUserMessageId' in handoff ? handoff.rootUserMessageId : undefined)) throw new Error('This continuation belongs to an older request. Select the output again.');
                                      const brief = listContinuationBrief(handoff, { projectId: listReferenceProjectId, sessionId,
                                        userId: kernel.current.getUserContext().dbUserId ?? '', currentPrompt: listReferenceInstruction, rootTimestamp: currentHuman?.timestamp ?? '' });
                                      await hydrateListOutputMentions(kernel.current, listReferenceProjectId, brief, currentAssistantAbortController.signal);
                                      currentAssistantAbortController.signal.throwIfAborted();
                                      if (kernel.current.getActiveChatSessionId() !== sessionId || kernel.current.getLastProjectId(sessionId) !== listReferenceProjectId
                                        || kernel.current.getUserContext().dbUserId !== ('userId' in handoff ? handoff.userId : undefined)
                                        || kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested) throw new Error('The conversation changed while resolving the previous edit.');
                                      generationMentions = resolveListOutputMentions(brief, listOutputMentionOptions(kernel.current, listReferenceProjectId));
                                      generationMentions.editSourceUrl = generationMentions.outputs[0]?.url;
                                      currentTurnSourceUrls.push(...generationMentions.outputs.map(output => output.url));
                                    }
                                    let creativeExamples: ReturnType<typeof listOutputMentionOptions> = [];
                                    const exampleIds = scriptPayload.listCreativeReferences?.filter(reference => reference.kind === 'output').map(reference => reference.id) ?? [];
                                    if (isListTurn && listReferenceProjectId && exampleIds.length) {
                                      const examplePrompt = exampleIds.map(id => `@output:${id}`).join(' ');
                                      const userId = kernel.current.getUserContext().dbUserId;
                                      await hydrateListOutputMentions(kernel.current, listReferenceProjectId, examplePrompt, currentAssistantAbortController.signal);
                                      currentAssistantAbortController.signal.throwIfAborted();
                                      if (kernel.current.getActiveChatSessionId() !== sessionId || kernel.current.getLastProjectId(sessionId) !== listReferenceProjectId
                                        || kernel.current.getUserContext().dbUserId !== userId || kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested) throw new Error('The conversation changed while resolving reference examples.');
                                      creativeExamples = resolveListOutputMentions(examplePrompt, listOutputMentionOptions(kernel.current, listReferenceProjectId)).outputs;
                                    }
                                    // A saved output the agent designated (the open one, "her", "Output 10") becomes the edit base.
                                    // The server still proves it is a saved output of this project before any provider call.
                                    const designatedOutputIds = isListTurn && listReferenceProjectId && !generationMentions.editSourceUrl && !currentTurnSourceUrls.length
                                      ? [...new Set(scriptPayload.commands.flatMap(command => command.action_type === 'GENERATE_MEDIA' || command.action_type === 'GENERATE_VARIATIONS'
                                        ? [command.parameters?.sourceOutputId, (command.parameters?.parameterOverrides as Record<string, unknown> | undefined)?.sourceOutputId] : [])
                                        .filter((id): id is string => typeof id === 'string' && /^[\w-]{1,128}$/.test(id)))]
                                      : [];
                                    if (designatedOutputIds.length > 1) throw new Error('Choose one output to start from.');
                                    // A video model that cannot start from text, given no picture: the output open in the viewer is
                                    // the one meant ("animate her", "here"). Without it the run is refused instead of guessed.
                                    if (isListTurn && listReferenceProjectId && !designatedOutputIds.length && !generationMentions.editSourceUrl && !currentTurnSourceUrls.length
                                      && scriptPayload.commands.some(command => command.action_type === 'GENERATE_MEDIA')) {
                                      const selectedModel = scriptPayload.commands.findLast(command => command.action_type === 'SELECT_MODEL')?.parameters?.modelId;
                                      const videoModel = typeof selectedModel === 'string' ? selectedModel : kernel.current.getDiffusionModel();
                                      const focusedOutputId = videoModelNeedsStartImage(videoModel) ? getProjectFocusedListOutputId(kernel.current, listReferenceProjectId) : null;
                                      if (focusedOutputId) designatedOutputIds.push(focusedOutputId);
                                    }
                                    if (designatedOutputIds.length === 1 && listReferenceProjectId) {
                                      const designatedPrompt = `@output:${designatedOutputIds[0]}`;
                                      const userId = kernel.current.getUserContext().dbUserId;
                                      await hydrateListOutputMentions(kernel.current, listReferenceProjectId, designatedPrompt, currentAssistantAbortController.signal);
                                      currentAssistantAbortController.signal.throwIfAborted();
                                      if (kernel.current.getActiveChatSessionId() !== sessionId || kernel.current.getLastProjectId(sessionId) !== listReferenceProjectId
                                        || kernel.current.getUserContext().dbUserId !== userId || kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested) throw new Error('The conversation changed while resolving the output to start from.');
                                      const designated = resolveListOutputMentions(designatedPrompt, listOutputMentionOptions(kernel.current, listReferenceProjectId)).outputs[0]!;
                                      generationMentions = { ...generationMentions, outputs: [...generationMentions.outputs, designated], editSourceUrl: designated.url };
                                      currentTurnSourceUrls.push(designated.url);
                                    }
                                    processedScriptForStream = {
                                        id: scriptPayload.id || uuidv4(),
                                        label: scriptPayload.label,
                                        commands: scriptPayload.commands.map((cmd): UiCommand => {
                                            const normalizedCommand = isListTurn
                                              ? bindListGenerationReferences(cmd, currentTurnSourceUrls, generationMentions.outputs, generationMentions.editSourceUrl, creativeExamples)
                                              : cmd;
                                            return {
                                              id: uuidv4(), status: 'pending',
                                              action_type: normalizedCommand.action_type as string,
                                              parameters: normalizedCommand.parameters as Record<string, unknown>,
                                              description: cmd.description,
                                            };
                                        }),
                                        autorun: scriptPayload.autorun ?? true,
                                        autoRecall: resolvedAutoRecall,
                                        studioViewMode: projectContextForThisTurn?.studioViewMode,
                                        isAgentGenerated: true,
                                        listGenerationAgreement: scriptPayload.listGenerationAgreement,
                                        listReferenceContinuation: handoff as ScriptInstance['listReferenceContinuation'],
                                        listCreativeReferences: scriptPayload.listCreativeReferences,
                                        listSceneSource: scriptPayload.listSceneSource,
                                        listCreativePlan: scriptPayload.listCreativePlan,
                                    };
                                    processedScriptForStream = removeMediaGenerationCommandsForPromptRewrite(
                                      processedScriptForStream,
                                      isUserTurn ? turnPrompt : '',
                                    );
                                    processedScriptForStream = removePlanningCommandsForAnswerOnlyRequest(
                                      processedScriptForStream,
                                      isUserTurn ? turnPrompt : '',
                                    );
                                    if (processedScriptForStream?.listGenerationAgreement) {
                                      kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { listGenerationAgreementScriptId: processedScriptForStream.id });
                                    }
                                    if (processedScriptForStream) {
                                      console.log('[Assistant SSE] Script autoRecall resolution:', {
                                        scriptId: processedScriptForStream.id,
                                        label: processedScriptForStream.label,
                                        rawAutoRecall: scriptPayload.autoRecall,
                                        autoRecallProvided,
                                        collaborationMode: turnCollaborationMode,
                                        forcedToFalseByAssisted: turnCollaborationMode === 'ASSISTED',
                                        resolvedAutoRecall,
                                        source:
                                          turnCollaborationMode === 'ASSISTED'
                                            ? 'forced_assisted_false'
                                            : autoRecallProvided
                                              ? 'payload'
                                              : 'default_true',
                                      });
                                    }
                                } else {
                                    processedScriptForStream = null;
                                }

                                // LIST projects hide the command cards ("ran N commands"
                                // / "launched on workspace") entirely: the user sees the
                                // model's thought + the outputs in the batch grid, never
                                // the mechanics. The thought content stays visible.
                                const hideScriptsForList = projectContextForThisTurn?.studioViewMode === 'marketing';
                                if (messageTimestampForStream) {
                                    kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
                                        msg.timestamp === messageTimestampForStream
                                            ? {
                                                ...msg,
                                                // Keep the LIST thought visible; retain reference lookup context.
                                                content: (userPreferences.actSilently !== false && processedScriptForStream && !hideScriptsForList) ? '' : msg.content,
                                                scriptsInstances: processedScriptForStream ? [processedScriptForStream] : [],
                                              }
                                            : msg
                                    ), finalTargetSessionId);
                                } else {
                                    console.warn("Script event received before assistantMessage event. Script will be attached at the end of the turn if a message appears.");
                                }
                                break;
                            }
                            case 'thoughts': {
                                const thoughtText = event.payload as string;
                                thoughtsForStream += thoughtText;
                                if (messageTimestampForStream) {
                                    kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
                                        msg.timestamp === messageTimestampForStream
                                            ? { ...msg, thoughts: thoughtsForStream }
                                            : msg
                                    ), finalTargetSessionId);
                                }
                                const headings = [...thoughtsForStream.matchAll(/(?:^|\n)[ \t]*\*\*([^*\r\n]{1,120})\*\*[ \t]*(?=\r?\n|$)/g)];
                                const heading = headings.at(-1)?.[1]?.trim();
                                if (heading && heading !== lastActivityHeading) {
                                    lastActivityHeading = heading;
                                    kernel.current.setSessionStatus(sessionId, heading);
                                }
                                break;
                            }
                        }
                    } catch (e) {
                        if (!(e instanceof SyntaxError)) throw e;
                        console.error("Failed to parse SSE event JSON:", e);
                    }
                }
            }
            if (errorOccurred) break;
        }
        
        if (processedScriptForStream && messageTimestampForStream) {
            kernel.current?.updateActiveSessionMessages(prev => {
                const messageExists = prev.some(m => m.timestamp === messageTimestampForStream);
                if (!messageExists) return prev;
                
                // Retain lookup results for the next turn; Chat hides their cards.
                const hideScriptsForListFlush = projectContextForThisTurn?.studioViewMode === 'marketing';
                return prev.map(msg => {
                    if (msg.timestamp === messageTimestampForStream) {
                        let finalContent = msg.content;
                        if (userPreferences.actSilently !== false && processedScriptForStream && !hideScriptsForListFlush) {
                            finalContent = '';
                        }
                        return {
                          ...msg,
                          content: finalContent,
                          scriptsInstances: (msg.scriptsInstances && msg.scriptsInstances.length > 0
                                ? msg.scriptsInstances
                                : [processedScriptForStream!]),
                        };
                    }
                    return msg;
                });
            }, finalTargetSessionId);
        }
      }
    } catch (error: any) {
      errorOccurred = true;
      if (!currentAssistantAbortController.signal.aborted) {
        kernel.current.handleError(error instanceof Error ? error.message : 'The assistant could not complete this request. Please try again.');
      }
    } finally {
      kernel.current.setSessionStatus(sessionId, '');
      // After a completed turn, give the session an intelligent LLM title (once,
      // only if still default-named). Best-effort, fire-and-forget.
      if (!errorOccurred && !listRetryAllowanceForTurn) {
        void kernel.current.maybeAutoTitleSession?.(sessionId);
      }
      patchSessionRuntimeIfAbortControllerOwner(
        kernel.current,
        sessionId,
        currentAssistantAbortController,
        { abortController: null }
      );

      if (kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested || errorOccurred) {
        shouldProcessAndRunScripts = false;
        if (isUserTurn) kernel.current?.setImagesInChatInput?.([]);
      }
      if (restoreComposerAfterRefusal && isUserTurn) {
        kernel.current?.setChatPrompt(promptOrGoalOrInstruction);
        kernel.current?.setImagesInChatInput?.(images);
      }
    }

    if (shouldProcessAndRunScripts) {
      if (processedScriptForStream && !messageTimestampForStream) {
          console.warn("Script was received, but no assistant message. Creating a default message container.");
          const fallbackTimestamp = new Date().toISOString();
          messageTimestampForStream = fallbackTimestamp;
          const imageUrlsForAssistantMessage: string[] = [];
          systemContextImagesForThisTurn.forEach(img => {
              if (img.url) imageUrlsForAssistantMessage.push(img.url);
          });
          const sessionSnapshot = finalTargetSessionId
            ? (kernel.current?.getInventory().ChatSession.registry[finalTargetSessionId] ?? null)
            : null;
          const messageTarget = resolveMessageTargetFromSession(sessionSnapshot);
          const fallbackMessage: Message = {
              content: '',
              role: 'assistant',
              timestamp: fallbackTimestamp,
              assistantTurnId: turnIdForStream || undefined,
              agentName: 'Fater',
              thoughts: thoughtsForStream,
              scriptsInstances: [processedScriptForStream],
              imageUrls: Array.from(new Set(imageUrlsForAssistantMessage)),
              ...messageTarget,
          };
          kernel.current?.updateActiveSessionMessages(prev => [...prev, fallbackMessage], finalTargetSessionId);
      }

      let processedScript = processedScriptForStream;

      const assistantScriptForTrace = processedScript
        ? {
            id: processedScript.id,
            label: processedScript.label,
            autorun: processedScript.autorun,
            executeCount: processedScript.executeCount,
            isAgentGenerated: processedScript.isAgentGenerated,
            isManuallyInvoked: processedScript.isManuallyInvoked,
            commands: processedScript.commands.map(cmd => ({
              id: cmd.id,
              action_type: cmd.action_type,
              parameters: cmd.parameters,
              description: cmd.description,
              status: cmd.status,
              error: cmd.error,
            })),
          }
        : null;

      if (finalTargetSessionId && messageTimestampForStream) {
        if (processedScript && requiresListScriptPersistence(processedScript)) {
          const runtime = kernel.current.readEntityRuntimeState('ChatSession', sessionId);
          if (runtime.abortController && runtime.abortController !== currentAssistantAbortController) return;
          if (runtime.isStopRequested) currentAssistantAbortController.abort();
          kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { abortController: currentAssistantAbortController,
            chatState: [...(runtime.chatState ?? []).filter(step => step.settled === true), { text: 'Preparing your image', startTime: Date.now() }] });
          const persistence = emitAssistantMessageEventFromSession(kernel.current, finalTargetSessionId, messageTimestampForStream);
          try { await waitForListMessagePersistence(persistence, currentAssistantAbortController.signal); }
          catch (error) { if (currentAssistantAbortController.signal.aborted) return; throw error; }
          finally { patchSessionRuntimeIfAbortControllerOwner(kernel.current, sessionId, currentAssistantAbortController, { abortController: null,
            chatState: (runtime.chatState ?? []).filter(step => step.settled === true) }); }
        } else void emitAssistantMessageEventFromSession(kernel.current, finalTargetSessionId, messageTimestampForStream);
      }

      const shouldLogAssistantTurn =
        assistantResponseContent.length > 0 ||
        !!assistantScriptForTrace ||
        thoughtsForStream.length > 0;

      if (shouldLogAssistantTurn) {
        const sessionTypeForTrace =
          finalTargetSessionId && kernel.current
            ? (
                resolveProjectIdFromChatSessionContext(
                  kernel.current.getInventory().ChatSession.registry[finalTargetSessionId]
                )
                  ? 'project-linked'
                  : 'standalone'
              )
            : null;

        const assistantTurnPayload: AssistantTurnTracePayload = {
          turnId: turnIdForStream,
          chatSessionId: finalTargetSessionId ?? null,
          sessionType: sessionTypeForTrace ?? null,
          workspaceId,
          agentMode,
          userPrompt: isUserTurn ? promptOrGoalOrInstruction : (assistBody?.prompt ?? null),
          assistantResponse: assistantResponseContent,
          responseThoughts: thoughtsForStream || null,
          assistantScript: assistantScriptForTrace as ScriptInstance | null,
          metadata: {
            projectId: projectContextForThisTurn?.projectId ?? null,
            isUserTurn,
            hasScript: Boolean(assistantScriptForTrace),
          },
        };

        void persistAssistantTurnTrace(assistantTurnPayload);
      }

      if (kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested) {
        if (isUserTurn) kernel.current?.setImagesInChatInput?.([]);
        return;
      }

      let script: Promise<void> | undefined;
      if (processedScript && processedScript.autorun) {
        if (processedScript.listGenerationAgreement?.proposal) return;
        const allowance = kernel.current.readEntityRuntimeState('ChatSession', sessionId).listRetryAllowance;
        if (allowance?.claim !== listRetryAllowanceForTurn?.claim) return;
        const claimListRetryAllowance = projectContextForThisTurn?.studioViewMode === 'marketing'
          && allowance?.projectId === projectContextForThisTurn.projectId
          && allowance.userId === kernel.current.getUserContext().dbUserId ? allowance.claim : undefined;
        if (allowance && !claimListRetryAllowance) return;
        script = kernel.current.runGeneratedScriptLogic(workspaceId, processedScript, onScriptComplete, { sessionId, claimListRetryAllowance, listRetrySourceTaskId: allowance?.sourceTaskId }).catch(scriptError => {
          console.error(`[useAgents] Error autorunning script "${processedScript.label}":`, scriptError);
        });
      }

      if (isUserTurn) kernel.current?.setImagesInChatInput?.([]);
      if (script) return { script };
    }
  }, [ kernel, describeState, gatherProjectContext, getProjectIdFromWorkspaceId, getProjectIdFromSessionId ]);

  // The server holds the project for this turn from its request on (lib/designer/billing.server.ts): the
  // hold is touched while a pass or its script runs and released once its chain is over, stopped included.
  const legacyChains = useRef(new Map<string, LegacyChain>());
  const executeLegacyTurn = useCallback(async (...args: LegacyTurnArgs<typeof _executeAssistantTurn>): Promise<void> => {
    const [sessionId, workspaceId, , , isUserTurn] = args;
    const projectId = getProjectIdFromSessionId(sessionId) ?? getProjectIdFromWorkspaceId(workspaceId);
    const chain = joinLegacyChain(legacyChains.current, sessionId, isUserTurn, uuidv4);
    const { turnId } = chain;
    const hold = (kind: 'touch' | 'stop') => fetchApi('/api/ai/designer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ brain: 'legacy', kind, sessionId, projectId, turnId }),
      // A release sent while the page unloads still lands.
      ...(kind === 'stop' ? { keepalive: true } : {}),
    }).catch(() => undefined);
    const heartbeat = projectId ? setInterval(() => void hold('touch'), DESIGNER_TURN_HEARTBEAT_S * 1000) : undefined;
    try {
      await (await _executeAssistantTurn(turnId, ...args))?.script;
    } finally {
      clearInterval(heartbeat);
      if (leaveLegacyChain(legacyChains.current, sessionId, chain) && projectId) void hold('stop');
    }
  }, [_executeAssistantTurn, getProjectIdFromSessionId, getProjectIdFromWorkspaceId]);


  // Answer to the question `askAssistant` (kernel/useAgents.ts) asks when it opens the
  // 'DetachedStateClarification' modal: either realign the workspace on its validated version, or
  // promote the historical version the user is looking at, then resume the interrupted turn.
  const resolveDetachedState = useCallback((
    mode: 'latest' | 'promote',
    payload: DetachedStateClarificationPayload,
  ): void => {
    if (!kernel.current || !payload) return;

    const activeId = kernel.current.getActiveChatSessionId();
    if (!activeId) return;

    const { workspaceId, historicalVersionId, prompt, imageUrls, onScriptComplete } = payload;
    const { getWorkspace, applyWorkspaceVersion, promoteVersion, closeModal, handleError } = kernel.current;

    let validatedVersionId: string | undefined;
    if (mode === 'latest') {
      const workspace = getWorkspace(workspaceId);
      if (!workspace || !workspace.validatedVersionId) {
        handleError("Could not find workspace or its validated version.");
        closeModal();
        return;
      }
      validatedVersionId = workspace.validatedVersionId;
    }

    try {
      if (validatedVersionId) {
        applyWorkspaceVersion(workspaceId, validatedVersionId);
      } else {
        promoteVersion(workspaceId, historicalVersionId);
      }
      kernel.current.askAssistant(activeId, prompt, imageUrls, onScriptComplete, {
        skipDetachedCheck: true,
        targetWorkspaceId: workspaceId,
        agentMode: 'director',
      });
    } catch (error) {
      console.error(
        mode === 'latest'
          ? "Failed to go to latest and continue:"
          : "Failed to promote version and continue:",
        error,
      );
      handleError(
        mode === 'latest'
          ? "Failed to switch to the latest version."
          : "Failed to make this the main version.",
      );
    } finally {
      closeModal();
    }
  }, [kernel]);

  return { executeLegacyTurn, resolveDetachedState };
}
